import React, { useState, useEffect, useRef } from 'react';
import { Loader } from '@orijinworks/frontend-commons';
import { fetchJanusToken } from '../../../util/APIUtils';
import { setJanusToken } from '../../../constants';

/**
 * HOC that fetches the Janus token before rendering the wrapped component.
 * Used for Feeds so no feed hooks/components run until the token API has returned.
 * State: rolePermissionReady - true after fetchJanusToken() response (success or error).
 */
const withRolePermission = (WrappedComponent) => {
  const WithRolePermission = (props) => {
    const [rolePermissionReady, setRolePermissionReady] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
      mountedRef.current = true;

      const loadToken = async () => {
        try {
          const response = await fetchJanusToken();
          if (mountedRef.current && response?.token) {
            setJanusToken(response.token);
          }
        } catch (error) {
          // Unblock on error so UI can render; downstream handles missing token
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[withRolePermission] fetchJanusToken failed:', error?.message ?? error);
          }
        } finally {
          if (mountedRef.current) {
            setRolePermissionReady(true);
          }
        }
      };

      loadToken();

      return () => {
        mountedRef.current = false;
      };
    }, []);

    if (!rolePermissionReady) {
      return <Loader isLoading />;
    }

    return <WrappedComponent {...props} />;
  };

  WithRolePermission.displayName = `WithRolePermission(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithRolePermission;
};

export default withRolePermission;
